import { createClient, type User } from '@supabase/supabase-js';

/* ══════════════════════════════════════════════
   SUPABASE INIT
══════════════════════════════════════════════ */
const sb = createClient(
  'https://tvvxiwwzksikrjnkthgp.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InR2dnhpd3d6a3Npa3Jqbmt0aGdwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQwNDM3NTIsImV4cCI6MjA4OTYxOTc1Mn0.2IePOeMJsiS5f5ZJuDdnSe-sVPEVsOHUw7Pa_xQFMwY'
);

const FREE_LIMIT = 2;
const CHAR_LIMIT = 300;
const ADMIN_EMAIL = 'divineawuku87@gmail.com';
const SUPPORT_EMAIL = 'divineasare.org@gmail.com';

/* ══════════════════════════════════════════════
   TYPES
══════════════════════════════════════════════ */
type MediaType = 'photo' | 'video';
type SearchMode = 'photo' | 'video' | 'both';

interface MediaItem {
  type: MediaType;
  id: string;
  thumb: string;
  download?: string;
  videoUrl?: string;
  credit?: string;
  source: 'Pexels' | 'Pixabay' | 'Unsplash';
}

interface ScriptLine {
  id: number;
  text: string;
  keyword: string;
  results: MediaItem[];
  fetched: boolean;
}

interface ProjectSnapshot {
  lines: ScriptLine[];
  picked: Record<number, MediaItem>;
  uid: number;
}

interface Project {
  id: string;
  name: string;
  updated_at: string;
  data: ProjectSnapshot;
}

interface WaitlistEntry {
  email: string;
  created_at: string;
}

/* ══════════════════════════════════════════════
   STATE
══════════════════════════════════════════════ */
let currentUser: User | null = null;
let searchesUsed = 0;
let lines: ScriptLine[] = [];
let picked: Record<number, MediaItem> = {};
const busy = new Set<number>();
let uid = 0;
let currentProjectId: string | null = null;
let projects: Project[] = [];
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
let _toastTimer: ReturnType<typeof setTimeout> | null = null;

/* ══════════════════════════════════════════════
   API KEYS
══════════════════════════════════════════════ */
const PEXELS_KEY = 'UObycStEMRpWezOWYjmfpsNyw4bpRf8woZgGb3IjIAKVz2bp4jxi76lK';
const PIXABAY_KEY = '28269-e71c7fc5a286b46109411ae00';
const UNSPLASH_KEY = '73AScB3cqj5Oas0_f3eR0IZUxalM0y_20SKfChFnvPg';

const getKey = (): string => PEXELS_KEY;
// eslint-disable-next-line @typescript-eslint/no-empty-function
function onKeyInput(): void {}
void onKeyInput;

/* ══════════════════════════════════════════════
   PAGE ROUTING
══════════════════════════════════════════════ */
function showPage(name: string): void {
  document.querySelectorAll<HTMLElement>('.page').forEach(p => p.classList.remove('active'));
  document.getElementById(name + 'Page')?.classList.add('active');
  if (name === 'landing') requestAnimationFrame(() => requestAnimationFrame(tryInitLanding));
}

function showAuth(tab?: string): void {
  showPage('auth');
  switchAuthTab(tab || 'login');
}

/* ══════════════════════════════════════════════
   AUTH INIT — check session on load
══════════════════════════════════════════════ */
(async () => {
  try {
    const { data: { session } } = await sb.auth.getSession();
    if (session?.user) {
      currentUser = session.user;
      await enterApp();
    } else {
      showPage('landing');
    }
  } catch (e) {
    console.error('Session check failed:', e);
    showPage('landing');
  }
})();

/* ══════════════════════════════════════════════
   AUTH ACTIONS
══════════════════════════════════════════════ */
function switchAuthTab(tab: string): void {
  const isLogin = tab === 'login';
  const loginEl = document.getElementById('authLogin');
  const signupEl = document.getElementById('authSignup');
  if (loginEl) loginEl.style.display = isLogin ? '' : 'none';
  if (signupEl) signupEl.style.display = isLogin ? 'none' : '';
  document.getElementById('authTabLogin')?.classList.toggle('on', isLogin);
  document.getElementById('authTabSignup')?.classList.toggle('on', !isLogin);
  const tl = document.getElementById('authTagline');
  if (tl) {
    tl.style.opacity = '0';
    tl.style.transform = 'translateY(-5px)';
    setTimeout(() => {
      tl.textContent = isLogin
        ? 'Good to see you again 👋'
        : 'Join thousands of creators saving hours on B-roll';
      tl.style.opacity = '1';
      tl.style.transform = 'translateY(0)';
    }, 160);
  }
}

function showAuthErr(msg: string, panel: 'login' | 'signup'): void {
  const id = panel === 'signup' ? 'authErrSignup' : 'authErrLogin';
  const okId = panel === 'signup' ? 'authOkSignup' : 'authOkLogin';
  const el = document.getElementById(id);
  const ok = document.getElementById(okId);
  if (el) { el.textContent = msg; el.style.display = 'block'; }
  if (ok) ok.style.display = 'none';
}

function showAuthOk(msg: string, panel: 'login' | 'signup'): void {
  const id = panel === 'signup' ? 'authOkSignup' : 'authOkLogin';
  const errId = panel === 'signup' ? 'authErrSignup' : 'authErrLogin';
  const el = document.getElementById(id);
  const err = document.getElementById(errId);
  if (el) { el.textContent = msg; el.style.display = 'block'; }
  if (err) err.style.display = 'none';
}

function hideAuthMsg(): void {
  ['authErrLogin', 'authOkLogin', 'authErrSignup', 'authOkSignup'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  });
}
void hideAuthMsg;

async function doLogin(): Promise<void> {
  const emailEl = document.getElementById('loginEmail') as HTMLInputElement | null;
  const passEl  = document.getElementById('loginPass')  as HTMLInputElement | null;
  const email = emailEl?.value.trim() ?? '';
  const pass  = passEl?.value ?? '';
  if (!email || !pass) { showAuthErr('Please fill in both fields.', 'login'); return; }
  const btn = document.getElementById('loginBtn') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'Logging in…'; }
  try {
    const { data, error } = await sb.auth.signInWithPassword({ email, password: pass });
    if (error) {
      const msg = error.message.includes('Invalid login')
        ? 'Wrong email or password.'
        : error.message.includes('confirm')
          ? 'Please confirm your email first — check your inbox.'
          : error.message;
      showAuthErr(msg, 'login');
      if (btn) { btn.disabled = false; btn.textContent = 'Log in →'; }
      return;
    }
    if (data?.session?.user) {
      currentUser = data.session.user;
      if (btn) { btn.disabled = false; btn.textContent = '✓ Welcome back!'; }
      showWelcome('WELCOME BACK', 'Your workspace is ready 🎬', '👋');
      setTimeout(() => enterApp(), 1900);
    } else {
      showAuthErr('Login failed. Please try again.', 'login');
      if (btn) { btn.disabled = false; btn.textContent = 'Log in →'; }
    }
  } catch {
    showAuthErr('Connection failed. Check your internet and try again.', 'login');
    if (btn) { btn.disabled = false; btn.textContent = 'Log in →'; }
  }
}

async function doSignup(): Promise<void> {
  const emailEl = document.getElementById('signupEmail') as HTMLInputElement | null;
  const passEl  = document.getElementById('signupPass')  as HTMLInputElement | null;
  const email = emailEl?.value.trim() ?? '';
  const pass  = passEl?.value ?? '';
  if (!email) { showAuthErr('Please enter your email.', 'signup'); return; }
  if (!pass)  { showAuthErr('Please enter a password.', 'signup'); return; }
  if (pass.length < 6) { showAuthErr('Password must be at least 6 characters.', 'signup'); return; }
  const btn = document.getElementById('signupBtn') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'Creating account…'; }
  try {
    const { error: signUpError } = await sb.auth.signUp({ email, password: pass });
    if (signUpError && !signUpError.message.includes('already registered')) {
      showAuthErr(signUpError.message, 'signup');
      if (btn) { btn.disabled = false; btn.textContent = 'Create free account →'; }
      return;
    }
    // After signup, immediately sign in (button stays in creating state briefly)
    const { data, error: signInError } = await sb.auth.signInWithPassword({ email, password: pass });
    if (signInError) {
      showAuthOk('Account created! Please log in.', 'signup');
      if (btn) { btn.disabled = false; btn.textContent = 'Create free account →'; }
      switchAuthTab('login');
      const loginEmailEl = document.getElementById('loginEmail') as HTMLInputElement | null;
      if (loginEmailEl) loginEmailEl.value = email;
      return;
    }
    if (data?.session?.user) {
      currentUser = data.session.user;
      // Reset usage for new accounts — don't carry over fingerprint from old sessions
      resetFpForNewAccount();
      spawnParticles();
      showWelcome("YOU'RE IN!", `Welcome — let's find your B-roll 🎬`, '🎉');
      setTimeout(() => enterApp(), 2100);
    }
  } catch {
    showAuthErr('Connection failed. Check your internet and try again.', 'signup');
    if (btn) { btn.disabled = false; btn.textContent = 'Create free account →'; }
  }
}

function resetFpForNewAccount(): void {
  try {
    // Clear all fingerprint keys so new account starts fresh
    Object.keys(localStorage)
      .filter(k => k.startsWith('fp_'))
      .forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

/* ══════════════════════════════════════════════
   LOGOUT — with survey modal
══════════════════════════════════════════════ */
function doLogout(): void {
  showLogoutSurvey();
}

function showLogoutSurvey(): void {
  document.getElementById('logoutSurveyModal')?.classList.add('show');
}

function closeLogoutSurvey(): void {
  document.getElementById('logoutSurveyModal')?.classList.remove('show');
}

async function submitLogoutSurvey(): Promise<void> {
  const reason = (document.querySelector<HTMLInputElement>('input[name="logoutReason"]:checked'))?.value || '';
  const feedbackEl = document.getElementById('logoutFeedback') as HTMLTextAreaElement | null;
  const feedback = feedbackEl?.value.trim() || '';

  // Save to Supabase feedback table (best effort)
  try {
    if (reason || feedback) {
      await sb.from('logout_feedback').insert({
        user_id: currentUser?.id,
        email: currentUser?.email,
        reason,
        feedback,
        created_at: new Date().toISOString(),
      });
    }
  } catch { /* non-fatal */ }

  closeLogoutSurvey();
  await performLogout();
}

async function skipLogoutSurvey(): Promise<void> {
  closeLogoutSurvey();
  await performLogout();
}

async function performLogout(): Promise<void> {
  try { await sb.auth.signOut(); } catch (e) { console.error(e); }
  currentUser = null;
  clearAll();
  showPage('landing');
}

/* ══════════════════════════════════════════════
   ENTER APP
══════════════════════════════════════════════ */
async function enterApp(): Promise<void> {
  showPage('app');
  updateNavUser();
  try {
    const todayStr = new Date().toDateString();
    Object.keys(localStorage)
      .filter(k => k.startsWith('fp_') && !k.includes(todayStr))
      .forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
  await loadUsage();
  await loadProjects();
}

function updateNavUser(): void {
  const emailEl = document.getElementById('navUserEmail');
  const avatarEl = document.getElementById('navUserAvatar');
  if (currentUser?.email) {
    const email = currentUser.email;
    const initial = email.charAt(0).toUpperCase();
    if (emailEl) emailEl.textContent = email;
    if (avatarEl) avatarEl.textContent = initial;
  }
}

/* ══════════════════════════════════════════════
   SETTINGS DROPDOWN
══════════════════════════════════════════════ */
function toggleSettingsMenu(): void {
  const menu = document.getElementById('settingsMenu');
  if (!menu) return;
  const isOpen = menu.classList.contains('open');
  // Close any other open menus first
  document.querySelectorAll('.dropdown-menu.open').forEach(m => m.classList.remove('open'));
  if (!isOpen) menu.classList.add('open');
}

function closeAllDropdowns(e: MouseEvent): void {
  const target = e.target as HTMLElement;
  if (!target.closest('.dropdown-wrap')) {
    document.querySelectorAll('.dropdown-menu.open').forEach(m => m.classList.remove('open'));
  }
}

document.addEventListener('click', closeAllDropdowns);

/* ══════════════════════════════════════════════
   PROJECTS
══════════════════════════════════════════════ */
function toggleProjPanel(): void {
  document.getElementById('projBar')?.classList.toggle('open');
}

async function loadProjects(): Promise<void> {
  try {
    const { data, error } = await sb
      .from('projects')
      .select('id, name, updated_at, data')
      .eq('user_id', currentUser!.id)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    projects = (data as Project[]) || [];
  } catch {
    projects = [];
  }
  renderProjList();
}

function renderProjList(): void {
  const list = document.getElementById('projList');
  if (!list) return;
  if (!projects.length) {
    list.innerHTML = '<div class="proj-empty">No saved projects yet.<br>Save your current work above.</div>';
    return;
  }
  list.innerHTML = projects.map(p => {
    const date = new Date(p.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const lineCount = p.data?.lines?.length ?? 0;
    const active = p.id === currentProjectId ? ' active-proj' : '';
    return `<div class="proj-item${active}" onclick="loadProject('${p.id}')">
      <span class="proj-item-name">${esc(p.name)}</span>
      <span class="proj-item-meta">${lineCount} lines · ${date}</span>
      <button class="proj-del" onclick="event.stopPropagation();deleteProject('${p.id}')" title="Delete">✕</button>
    </div>`;
  }).join('');
}

async function saveProject(silent = false): Promise<void> {
  if (!currentUser) return;
  const nameEl = document.getElementById('projNameIn') as HTMLInputElement | null;
  const name = nameEl?.value.trim() || 'Untitled project';
  if (nameEl) nameEl.value = name;
  const snapshot: ProjectSnapshot = {
    lines: lines.map(l => ({ id: l.id, text: l.text, keyword: l.keyword, results: l.results, fetched: l.fetched })),
    picked,
    uid,
  };
  try {
    if (currentProjectId) {
      const { error } = await sb.from('projects').update({
        name, data: snapshot, updated_at: new Date().toISOString(),
      }).eq('id', currentProjectId).eq('user_id', currentUser.id);
      if (error) throw error;
    } else {
      const { data, error } = await sb.from('projects').insert({
        user_id: currentUser.id, name, data: snapshot, updated_at: new Date().toISOString(),
      }).select().single();
      if (error) throw error;
      currentProjectId = (data as Project).id;
    }
    await loadProjects();
    if (!silent) { toast('Project saved ✓', 'ok'); flashSaved(); }
    else { flashSaved(); }
  } catch {
    if (!silent) toast('Save failed — check connection', 'err');
  }
}

function flashSaved(): void {
  const el = document.getElementById('projSaving');
  if (!el) return;
  el.classList.add('show');
  setTimeout(() => el.classList.remove('show'), 2000);
}

function scheduleAutoSave(): void {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  if (!currentUser || !lines.length) return;
  autoSaveTimer = setTimeout(() => saveProject(true), 3000);
}

async function loadProject(id: string): Promise<void> {
  const proj = projects.find(p => p.id === id);
  if (!proj) return;
  lines = []; picked = {}; busy.clear(); uid = 0;
  const linesList = document.getElementById('linesList');
  if (linesList) linesList.innerHTML = '';
  const rscroll = document.getElementById('rscroll');
  if (rscroll) rscroll.innerHTML = `<div class="empty-r" id="emptyEl"><div class="empty-icon">🎬</div><div class="empty-txt">Project loaded — hit Search All to refresh visuals.</div></div>`;

  const snap = proj.data;
  currentProjectId = id;
  const projNameIn = document.getElementById('projNameIn') as HTMLInputElement | null;
  if (projNameIn) projNameIn.value = proj.name;
  uid = snap.uid || 0;

  (snap.lines || []).forEach(l => {
    lines.push({ id: l.id, text: l.text, keyword: l.keyword, results: l.results || [], fetched: l.fetched || false });
    const el = document.createElement('div');
    el.className = 'lc'; el.id = `card-${l.id}`;
    el.innerHTML = cardHTML(l.id, l.text, l.keyword);
    linesList?.appendChild(el);
  });

  picked = snap.picked || {};

  lines.forEach(l => {
    if (l.results?.length) {
      renderBlock(l);
      setDot(l.id, picked[l.id] ? 'ok' : '');
      document.getElementById(`card-${l.id}`)?.classList.toggle('matched', !!picked[l.id]);
    }
  });

  updateCount();
  updateDlBar();
  const saBtn = document.getElementById('saBtn') as HTMLButtonElement | null;
  if (saBtn) saBtn.disabled = lines.length === 0;
  renderProjList();
  toast(`Loaded "${proj.name}"`, 'ok');
  if (window.innerWidth < 860) document.getElementById('projBar')?.classList.remove('open');
}

async function deleteProject(id: string): Promise<void> {
  const proj = projects.find(p => p.id === id);
  if (!proj) return;
  if (!confirm(`Delete "${proj.name}"? This can't be undone.`)) return;
  try {
    const { error } = await sb.from('projects').delete().eq('id', id).eq('user_id', currentUser!.id);
    if (error) throw error;
    if (currentProjectId === id) {
      currentProjectId = null;
      const projNameIn = document.getElementById('projNameIn') as HTMLInputElement | null;
      if (projNameIn) projNameIn.value = '';
    }
    await loadProjects();
    toast('Project deleted', '');
  } catch {
    toast('Delete failed', 'err');
  }
}

function newProject(): void {
  currentProjectId = null;
  const projNameIn = document.getElementById('projNameIn') as HTMLInputElement | null;
  if (projNameIn) projNameIn.value = '';
  clearAll();
  renderProjList();
  projNameIn?.focus();
  toast('New project started', 'ok');
}

/* ══════════════════════════════════════════════
   USAGE — Fixed: daily reset by reading from DB fresh each login
══════════════════════════════════════════════ */
async function loadUsage(): Promise<void> {
  try {
    // Always get fresh count from Supabase (daily reset is handled server-side by the RPC)
    const { data, error } = await sb.rpc('get_search_count', { p_user_id: currentUser!.id });
    if (error) {
      // Fallback to fingerprint only if Supabase fails
      searchesUsed = getFpCount();
    } else {
      searchesUsed = (data as number) || 0;
      // Sync fingerprint to DB count (DB is authoritative for new accounts)
      const fpCount = getFpCount();
      // Only use fp if it's higher AND user has been using the app today
      if (fpCount > searchesUsed && fpCount <= FREE_LIMIT * 2) {
        searchesUsed = fpCount;
      }
    }
  } catch {
    searchesUsed = getFpCount();
  }
  updateUsageUI();
}

function updateUsageUI(): void {
  const pct = Math.min(100, (searchesUsed / FREE_LIMIT) * 100);
  const fill = document.getElementById('usageFill') as HTMLElement | null;
  const txt  = document.getElementById('usageTxt')  as HTMLElement | null;
  const pill = document.getElementById('usagePill') as HTMLElement | null;
  if (currentUser?.email === ADMIN_EMAIL) {
    if (pill) pill.style.background = 'rgba(0,232,162,.08)';
    if (fill) { fill.style.width = '100%'; fill.className = 'usage-fill'; fill.style.background = 'var(--green)'; }
    if (txt) txt.textContent = '∞ Unlimited — Admin';
    return;
  }
  if (fill) { fill.style.width = pct + '%'; fill.className = 'usage-fill' + (pct >= 100 ? ' full' : pct >= 50 ? ' warn' : ''); }
  if (txt) txt.textContent = `${searchesUsed} / ${FREE_LIMIT} free searches today`;
}

async function incrementUsage(): Promise<void> {
  incFpCount();
  try {
    const { data } = await sb.rpc('increment_search_count', { p_user_id: currentUser!.id });
    const supabaseCount = (data as number) || searchesUsed + 1;
    const fpCount = getFpCount();
    searchesUsed = Math.max(supabaseCount, fpCount);
  } catch {
    searchesUsed = Math.max(searchesUsed + 1, getFpCount());
  }
  updateUsageUI();
}

function isAtLimit(): boolean {
  if (currentUser?.email === ADMIN_EMAIL) return false;
  return Math.max(searchesUsed, getFpCount()) >= FREE_LIMIT;
}

function showLimitModal(): void { document.getElementById('limitModal')?.classList.add('show'); }
function closeModal(): void { document.getElementById('limitModal')?.classList.remove('show'); }

/* ══════════════════════════════════════════════
   CHAR COUNT
══════════════════════════════════════════════ */
function updateCharCount(): void {
  const ta  = document.getElementById('pasteTA') as HTMLTextAreaElement | null;
  const el  = document.getElementById('charLimit');
  const usedEl = document.getElementById('charUsed');
  if (!ta) return;
  const used = ta.value.length;
  const isAdmin = currentUser?.email === ADMIN_EMAIL;
  if (usedEl) usedEl.textContent = String(used);
  if (isAdmin) {
    if (el) { el.className = 'char-limit'; el.innerHTML = `<span>${used}</span> characters — unlimited`; }
    ta.classList.remove('over');
    return;
  }
  if (el) {
    el.className = 'char-limit' + (used > CHAR_LIMIT ? ' over' : used > CHAR_LIMIT * 0.8 ? ' warn' : '');
    el.innerHTML = `<span id="charUsed">${used}</span> / ${CHAR_LIMIT} characters`;
  }
  ta.classList.toggle('over', used > CHAR_LIMIT);
}

/* ══════════════════════════════════════════════
   TAB SWITCH
══════════════════════════════════════════════ */
function switchTab(n: 1 | 2): void {
  const m1 = document.getElementById('m1');
  const m2 = document.getElementById('m2');
  if (m1) m1.style.display = n === 1 ? '' : 'none';
  if (m2) m2.style.display = n === 2 ? '' : 'none';
  document.getElementById('t1')?.classList.toggle('on', n === 1);
  document.getElementById('t2')?.classList.toggle('on', n === 2);
}

/* ══════════════════════════════════════════════
   SCRIPT STATE
══════════════════════════════════════════════ */
function doPaste(): void {
  const ta = document.getElementById('pasteTA') as HTMLTextAreaElement | null;
  if (!ta) return;
  const raw = ta.value.trim();
  if (!raw) { toast('Nothing to parse', 'err'); return; }
  const isAdmin = currentUser?.email === ADMIN_EMAIL;
  if (!isAdmin && raw.length > CHAR_LIMIT) { toast(`Script exceeds ${CHAR_LIMIT} character limit for free tier`, 'err'); return; }
  const chunks = raw.split(/\n{2,}/)
    .flatMap(p => p.replace(/([.!?])\s+(?=[A-Z"'])/g, '$1\n').split('\n').map(s => s.trim()).filter(s => s.length > 6));
  if (!chunks.length) { toast('Could not find any lines', 'err'); return; }
  chunks.forEach(addLine);
  ta.value = '';
  updateCharCount();
  toast(`Added ${chunks.length} lines`, 'ok');
}

function doAdd(): void {
  const ta = document.getElementById('typeTA') as HTMLTextAreaElement | null;
  if (!ta) return;
  const t = ta.value.trim();
  if (!t) { ta.focus(); return; }
  t.split('\n').map(s => s.trim()).filter(s => s).forEach(addLine);
  ta.value = '';
  ta.focus();
  toast('Line added', 'ok');
}

function addLine(text: string): void {
  const id = uid++;
  lines.push({ id, text, keyword: kwFrom(text), results: [], fetched: false });
  const el = document.createElement('div');
  el.className = 'lc'; el.id = `card-${id}`;
  el.innerHTML = cardHTML(id, text, kwFrom(text));
  document.getElementById('linesList')?.appendChild(el);
  updateCount();
  const saBtn = document.getElementById('saBtn') as HTMLButtonElement | null;
  if (saBtn) saBtn.disabled = false;
}

function cardHTML(id: number, text: string, kw: string): string {
  return `
    <span class="lnum">${String(id + 1).padStart(2, '0')}</span>
    <span class="ltxt" title="${escA(text)}" onclick="focusRes(${id})">${esc(text)}</span>
    <div class="sdot" id="dot-${id}"></div>
    <input class="kwin" id="kw-${id}" value="${escA(kw)}" placeholder="keyword…"
      onkeydown="if(event.key==='Enter'){event.stopPropagation();searchLine(${id})}"/>
    <button class="kbtn" id="kb-${id}" onclick="searchLine(${id})">Search</button>
    <button class="xbtn" onclick="removeLine(${id})">✕</button>`;
}

function removeLine(id: number): void {
  lines = lines.filter(l => l.id !== id);
  delete picked[id];
  document.getElementById(`card-${id}`)?.remove();
  document.getElementById(`res-${id}`)?.remove();
  updateCount(); updateDlBar();
  const saBtn = document.getElementById('saBtn') as HTMLButtonElement | null;
  if (!lines.length && saBtn) saBtn.disabled = true;
}

function updateCount(): void {
  const el = document.getElementById('lcount');
  if (el) el.textContent = `${lines.length} line${lines.length !== 1 ? 's' : ''}`;
}

/* ══════════════════════════════════════════════
   SMART KEYWORD EXTRACTION
══════════════════════════════════════════════ */
function kwFrom(text: string): string {
  const raw = text.toLowerCase().replace(/[^a-z0-9\s\-']/g, ' ').replace(/\s+/g, ' ').trim();
  const words = raw.split(' ').filter(w => w.length > 1);

  const stop = new Set([
    'the','a','an','and','or','but','in','on','at','to','for','of','with',
    'is','was','are','were','be','been','being','have','has','had','do','does',
    'did','will','would','could','should','may','might','shall','must','can',
    'it','its','this','that','these','those','they','them','their','there',
    'from','by','as','he','she','we','you','i','his','her','our','my','your',
    'not','no','so','than','then','when','who','what','how','all','said',
    'says','say','just','very','really','also','about','after','before',
    'over','under','between','through','into','onto','upon','within','without',
    'here','where','why','which','while','during','since','until','though',
    'although','because','however','therefore','thus','hence','yet','still',
    'even','only','both','each','every','any','some','such','more','most',
    'other','another','same','different','new','old','good','bad','great',
    'little','big','small','large','long','short','high','low','first','last',
    'one','two','three','many','much','few','several','own','like','make',
    'know','think','come','go','get','give','take','see','look','find','tell',
    'become','show','call','keep','seem','feel','try','leave','put','mean',
    'let','begin','turn','start','bring','want','need','use','say',
  ]);

  const visualBoost = new Set([
    'forest','ocean','mountain','river','desert','jungle','beach','sky','cloud',
    'sunset','sunrise','storm','rain','snow','fire','water','earth','space',
    'volcano','glacier','waterfall','cave','island','valley','cliff','lake',
    'city','street','bridge','road','highway','skyline','downtown',
    'factory','skyscraper','stadium','hospital','school','church','temple',
    'market','harbor','airport','tunnel','tower','wall','parliament',
    'courthouse','monument','ruins','construction','demolition',
    'crowd','protest','celebration','wedding','funeral','graduation','soldier',
    'doctor','scientist','farmer','athlete','child','family','elderly','worker',
    'police','refugee','immigrant','community',
    'robot','computer','phone','satellite','rocket','laboratory','microscope',
    'machine','energy','solar','nuclear','dna','virus','bacteria',
    'money','cash','stock','market','bank','inflation','poverty','wealth',
    'currency','gold','bitcoin','trading','economy',
    'war','battle','explosion','flood','earthquake','drought','famine','crisis',
    'disaster','riot','bombing','destruction',
    'lion','tiger','elephant','whale','shark','eagle','wolf','bear','deer',
    'horse','cattle','fish','bird','snake','insect','coral','penguin',
    'farming','harvest','crop','wheat','corn','rice','fruit','vegetable',
    'fishing','plantation','irrigation',
    'hope','freedom','justice','success','failure','growth','decline',
    'pollution','conservation','migration','evolution','revolution','innovation',
    'amazon','rainforest','deforestation','indigenous','tribe',
  ]);

  const abstract = new Set([
    'thing','things','stuff','way','ways','time','times','life','lives','world',
    'people','person','part','parts','place','places','point','points','fact',
    'facts','idea','ideas','case','cases','side','sides','kind','kinds','type',
    'types','form','forms','sense','level','levels','number','numbers','amount',
    'issue','issues','problem','problems','matter','matters','question','reason',
    'result','results','effect','effects','process','system','situation','area',
    'field','role','means','example','examples','aspect','aspects','terms',
  ]);

  function scoreWord(w: string): number {
    if (stop.has(w) || w.length < 3) return -99;
    let s = 0;
    if (visualBoost.has(w)) s += 10;
    if (abstract.has(w)) s -= 6;
    if (/[A-Z]/.test(text.split(' ').find(t => t.toLowerCase() === w) || '')) s += 4;
    if (w.length >= 5 && w.length <= 12) s += 2;
    if (w.length <= 3) s -= 2;
    if (/ing$/.test(w) && w.length > 5) s += 2;
    if (/(tion|sion|ment|ness|ism)$/.test(w)) s += 1;
    return s;
  }

  const scored = words
    .map(w => ({ w, s: scoreWord(w) }))
    .filter(x => x.s > -90)
    .sort((a, b) => b.s - a.s);

  let bestBigram: string | null = null;
  let bestBigramScore = -99;
  for (let i = 0; i < words.length - 1; i++) {
    const a = words[i], b = words[i + 1];
    if (stop.has(a) || stop.has(b)) continue;
    if (a.length < 3 || b.length < 3) continue;
    const s = scoreWord(a) + scoreWord(b) + 3;
    if (s > bestBigramScore) { bestBigramScore = s; bestBigram = `${a} ${b}`; }
  }

  const topWord = scored[0]?.w || '';
  const secondWord = scored.find(x => x.w !== topWord)?.w || '';

  if (bestBigram && bestBigramScore >= (scored[0]?.s || 0) + 1) return bestBigram;
  if (topWord && secondWord && secondWord !== topWord) return `${topWord} ${secondWord}`;
  return topWord || text.slice(0, 20);
}

/* ══════════════════════════════════════════════
   CACHE HELPERS
══════════════════════════════════════════════ */
async function getCached(keyword: string, mode: SearchMode): Promise<MediaItem[] | null> {
  try {
    const cacheKey = `${keyword.toLowerCase().trim()}:${mode}`;
    const { data, error } = await sb
      .from('search_cache')
      .select('results, cached_at')
      .eq('cache_key', cacheKey)
      .single();
    if (error || !data) return null;
    const age = Date.now() - new Date((data as { cached_at: string }).cached_at).getTime();
    if (age > 24 * 60 * 60 * 1000) return null;
    return (data as { results: MediaItem[] }).results;
  } catch { return null; }
}

async function setCached(keyword: string, mode: SearchMode, results: MediaItem[]): Promise<void> {
  try {
    const cacheKey = `${keyword.toLowerCase().trim()}:${mode}`;
    await sb.from('search_cache').upsert({
      cache_key: cacheKey, results, cached_at: new Date().toISOString(),
    }, { onConflict: 'cache_key' });
  } catch { /* non-fatal */ }
}

/* ══════════════════════════════════════════════
   FETCH SOURCES
══════════════════════════════════════════════ */
async function pxFetch(url: string, key: string): Promise<Record<string, unknown>> {
  const r = await fetch(url, { headers: { Authorization: key } });
  if (!r.ok) {
    const msg: Record<number, string> = { 401: 'Invalid Pexels key', 429: 'Rate limited — wait a moment' };
    throw new Error(msg[r.status] || `Pexels error ${r.status}`);
  }
  return r.json() as Promise<Record<string, unknown>>;
}

async function fetchFromPexels(kw: string, mode: SearchMode): Promise<MediaItem[]> {
  const k = getKey();
  let photos: MediaItem[] = [], videos: MediaItem[] = [];
  try {
    if (mode === 'both' || mode === 'photo') {
      const perPage = mode === 'photo' ? 10 : 5;
      const pr = await pxFetch(`https://api.pexels.com/v1/search?query=${enc(kw)}&per_page=${perPage}&orientation=landscape`, k) as { photos?: Array<{ id: number; src: { medium: string; large2x?: string; large?: string; original?: string }; photographer: string }> };
      photos = (pr.photos || []).map(p => ({
        type: 'photo' as MediaType, id: `px_${p.id}`, thumb: p.src.medium,
        download: p.src.large2x || p.src.large || p.src.original,
        credit: p.photographer, source: 'Pexels' as const,
      }));
    }
    if (mode === 'both' || mode === 'video') {
      const perPage = mode === 'video' ? 8 : 3;
      const vr = await pxFetch(`https://api.pexels.com/videos/search?query=${enc(kw)}&per_page=${perPage}&orientation=landscape`, k) as { videos?: Array<{ id: number; image: string; user?: { name: string }; video_files?: Array<{ quality: string; link: string }> }> };
      videos = (vr.videos || []).map(v => ({
        type: 'video' as MediaType, id: `px_v${v.id}`, thumb: v.image,
        videoUrl: (v.video_files || []).find(f => f.quality === 'sd')?.link || (v.video_files || [])[0]?.link,
        download: (v.video_files || []).find(f => f.quality === 'hd')?.link || (v.video_files || [])[0]?.link,
        credit: v.user?.name, source: 'Pexels' as const,
      }));
    }
  } catch (e) { console.warn('Pexels fetch failed:', (e as Error).message); }
  return [...photos, ...videos];
}

async function fetchFromPixabay(kw: string, mode: SearchMode): Promise<MediaItem[]> {
  let photos: MediaItem[] = [], videos: MediaItem[] = [];
  try {
    if (mode === 'both' || mode === 'photo') {
      const perPage = mode === 'photo' ? 10 : 5;
      const url = `https://pixabay.com/api/?key=${PIXABAY_KEY}&q=${enc(kw)}&image_type=photo&orientation=horizontal&per_page=${perPage}&safesearch=true`;
      const r = await fetch(url);
      if (r.ok) {
        const data = await r.json() as { hits?: Array<{ id: number; webformatURL: string; largeImageURL?: string; user: string }> };
        photos = (data.hits || []).map(h => ({
          type: 'photo' as MediaType, id: `pb_${h.id}`, thumb: h.webformatURL,
          download: h.largeImageURL || h.webformatURL,
          credit: h.user, source: 'Pixabay' as const,
        }));
      }
    }
    if (mode === 'both' || mode === 'video') {
      const perPage = mode === 'video' ? 8 : 3;
      const url = `https://pixabay.com/api/videos/?key=${PIXABAY_KEY}&q=${enc(kw)}&per_page=${perPage}&safesearch=true`;
      const r = await fetch(url);
      if (r.ok) {
        const data = await r.json() as { hits?: Array<{ id: number; user: string; videos?: { medium?: { thumbnail?: string; url?: string }; small?: { thumbnail?: string; url?: string }; large?: { url?: string } } }> };
        videos = (data.hits || []).map(h => ({
          type: 'video' as MediaType, id: `pb_v${h.id}`,
          thumb: h.videos?.medium?.thumbnail || h.videos?.small?.thumbnail || '',
          videoUrl: h.videos?.medium?.url || h.videos?.small?.url || '',
          download: h.videos?.large?.url || h.videos?.medium?.url || h.videos?.small?.url || '',
          credit: h.user, source: 'Pixabay' as const,
        }));
      }
    }
  } catch (e) { console.warn('Pixabay fetch failed:', (e as Error).message); }
  return [...photos, ...videos];
}

async function fetchFromUnsplash(kw: string, mode: SearchMode): Promise<MediaItem[]> {
  if (mode === 'video') return [];
  let photos: MediaItem[] = [];
  try {
    const perPage = mode === 'photo' ? 10 : 4;
    const url = `https://api.unsplash.com/search/photos?query=${enc(kw)}&per_page=${perPage}&orientation=landscape&client_id=${UNSPLASH_KEY}`;
    const r = await fetch(url);
    if (r.ok) {
      const data = await r.json() as { results?: Array<{ id: string; urls: { regular?: string; small?: string; full?: string }; user?: { name: string } }> };
      photos = (data.results || []).map(p => ({
        type: 'photo' as MediaType, id: `us_${p.id}`,
        thumb: p.urls.regular || p.urls.small || '',
        download: p.urls.full || p.urls.regular,
        credit: p.user?.name || 'Unsplash',
        source: 'Unsplash' as const,
      }));
    }
  } catch (e) { console.warn('Unsplash fetch failed:', (e as Error).message); }
  return photos;
}

async function fetchAllSources(kw: string, mode: SearchMode): Promise<MediaItem[]> {
  const [pexelsRes, pixabayRes, unsplashRes] = await Promise.allSettled([
    fetchFromPexels(kw, mode),
    fetchFromPixabay(kw, mode),
    fetchFromUnsplash(kw, mode),
  ]);
  const pexels   = pexelsRes.status   === 'fulfilled' ? pexelsRes.value   : [];
  const pixabay  = pixabayRes.status  === 'fulfilled' ? pixabayRes.value  : [];
  const unsplash = unsplashRes.status === 'fulfilled' ? unsplashRes.value : [];

  const pxPhotos = pexels.filter(r => r.type === 'photo');
  const pbPhotos = pixabay.filter(r => r.type === 'photo');
  const usPhotos = unsplash;
  const pxVideos = pexels.filter(r => r.type === 'video');
  const pbVideos = pixabay.filter(r => r.type === 'video');

  const photos: MediaItem[] = [];
  const maxP = Math.max(pxPhotos.length, pbPhotos.length, usPhotos.length);
  for (let i = 0; i < maxP; i++) {
    if (pxPhotos[i]) photos.push(pxPhotos[i]);
    if (usPhotos[i]) photos.push(usPhotos[i]);
    if (pbPhotos[i]) photos.push(pbPhotos[i]);
  }
  const videos: MediaItem[] = [];
  const maxV = Math.max(pxVideos.length, pbVideos.length);
  for (let i = 0; i < maxV; i++) {
    if (pxVideos[i]) videos.push(pxVideos[i]);
    if (pbVideos[i]) videos.push(pbVideos[i]);
  }
  return [...photos, ...videos];
}

/* ══════════════════════════════════════════════
   SEARCH
══════════════════════════════════════════════ */
async function searchLine(id: number): Promise<void> {
  if (!currentUser) { toast('Please log in first', 'err'); return; }
  if (isAtLimit()) { showLimitModal(); return; }
  const line = lines.find(l => l.id === id);
  if (!line) return;
  const kwEl  = document.getElementById(`kw-${id}`) as HTMLInputElement | null;
  const btnEl = document.getElementById(`kb-${id}`) as HTMLButtonElement | null;
  const kw = (kwEl ? kwEl.value.trim() : line.keyword) || line.keyword;
  if (!kw) return;
  line.keyword = kw;
  busy.add(id);
  if (btnEl) { btnEl.disabled = true; btnEl.textContent = '…'; }
  setDot(id, 'spin');
  try {
    const cached = await getCached(kw, 'both');
    if (cached) {
      line.results = cached; line.fetched = true;
      renderBlock(line); setDot(id, picked[id] ? 'ok' : ''); showRight();
      toast(`${line.results.length} visuals ⚡ (cached)`, 'ok');
      scheduleAutoSave(); return;
    }
    const results = await fetchAllSources(kw, 'both');
    line.results = results; line.fetched = true;
    await incrementUsage();
    await setCached(kw, 'both', results);
    renderBlock(line); setDot(id, picked[id] ? 'ok' : ''); showRight();
    toast(`${line.results.length} visuals for line ${id + 1}`, 'ok');
    scheduleAutoSave();
  } catch (e) {
    setDot(id, '');
    toast((e as Error).message, 'err');
  } finally {
    busy.delete(id);
    if (btnEl) { btnEl.disabled = false; btnEl.textContent = 'Search'; }
  }
}

async function searchAll(): Promise<void> {
  if (!currentUser) return;
  const btn = document.getElementById('saBtn') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = 'Searching…'; }
  for (let i = 0; i < lines.length; i += 2) {
    if (isAtLimit()) { showLimitModal(); break; }
    await Promise.all(lines.slice(i, i + 2).map(l => searchLine(l.id)));
    await wait(300);
  }
  if (btn) { btn.disabled = false; btn.textContent = 'Search All'; }
}

async function reSearch(lid: number, mode: SearchMode): Promise<void> {
  if (!currentUser) return;
  if (isAtLimit()) { showLimitModal(); return; }
  const line = lines.find(l => l.id === lid);
  if (!line) return;
  const kwEl = document.getElementById(`kw-${lid}`) as HTMLInputElement | null;
  const kw = (kwEl ? kwEl.value.trim() : line.keyword) || line.keyword;
  line.keyword = kw;
  const grid  = document.getElementById(`mg-${lid}`);
  const nores = document.querySelector<HTMLElement>(`#res-${lid} .nores`);
  const loadingMsg = `Searching ${mode === 'both' ? 'photos & videos' : mode + 's'}…`;
  if (grid) grid.innerHTML = `<div style="padding:1.5rem;text-align:center;color:var(--muted);font-size:.78rem;grid-column:1/-1">${loadingMsg}</div>`;
  if (nores) nores.textContent = loadingMsg;
  setDot(lid, 'spin');
  try {
    const results = await fetchAllSources(kw, mode);
    line.results = results; line.fetched = true;
    await incrementUsage();
    await setCached(kw, mode, results);
    renderBlock(line); setDot(lid, picked[lid] ? 'ok' : ''); showRight();
    const label = mode === 'photo' ? 'photos' : mode === 'video' ? 'videos' : 'results';
    toast(`${results.length} ${label} for line ${lid + 1}`, 'ok');
    scheduleAutoSave();
  } catch (e) {
    setDot(lid, '');
    toast((e as Error).message, 'err');
  }
}

/* ══════════════════════════════════════════════
   RENDER
══════════════════════════════════════════════ */
function renderBlock(line: ScriptLine): void {
  const scroll = document.getElementById('rscroll');
  if (!scroll) return;
  const hasPhotos = line.results.some(m => m.type === 'photo');
  const hasVideos = line.results.some(m => m.type === 'video');
  const html = `<div class="rb" id="res-${line.id}">
    <div class="rlbl">
      <span class="rnum">L${line.id + 1}</span>
      <span class="rlbl-txt">${esc(line.text)}</span>
      <div class="rfilters">
        ${line.results.length ? `
          <button class="rfbtn all on" id="rf-all-${line.id}" onclick="filterBlock(${line.id},'all')">All</button>
          ${hasPhotos ? `<button class="rfbtn photo" id="rf-photo-${line.id}" onclick="filterBlock(${line.id},'photo')">📷 Photos</button>` : ''}
          ${hasVideos ? `<button class="rfbtn video" id="rf-video-${line.id}" onclick="filterBlock(${line.id},'video')">▶ Videos</button>` : ''}
          <div class="rf-divider"></div>
        ` : ''}
        <button class="rfbtn resrch" onclick="reSearch(${line.id},'both')" title="Re-search both photos and videos">🔄</button>
        <button class="rfbtn search-type photo" onclick="reSearch(${line.id},'photo')" title="Search photos only">📷 only</button>
        <button class="rfbtn search-type video" onclick="reSearch(${line.id},'video')" title="Search videos only">▶ only</button>
      </div>
    </div>
    ${!line.results.length
      ? `<div class="nores">No results for "<strong>${esc(line.keyword)}</strong>" — try re-searching or edit the keyword on the left</div>`
      : `<div class="mgrid" id="mg-${line.id}">${line.results.map((m, i) => mItem(line.id, m, i)).join('')}</div>`}
  </div>`;
  const ex = document.getElementById(`res-${line.id}`);
  if (ex) { ex.outerHTML = html; return; }
  const blocks = [...scroll.querySelectorAll<HTMLElement>('.rb')];
  const after = blocks.find(b => parseInt(b.id.replace('res-', '')) > line.id);
  after ? after.insertAdjacentHTML('beforebegin', html) : scroll.insertAdjacentHTML('beforeend', html);
}

function filterBlock(lid: number, mode: 'all' | MediaType): void {
  (['all', 'photo', 'video'] as const).forEach(m => {
    document.getElementById(`rf-${m}-${lid}`)?.classList.toggle('on', m === mode);
  });
  const line = lines.find(l => l.id === lid);
  if (!line) return;
  line.results.forEach((m, i) => {
    const el = document.getElementById(`mi-${lid}-${i}`);
    if (!el) return;
    el.style.display = (mode === 'all' || m.type === mode) ? '' : 'none';
  });
}

function mItem(lid: number, m: MediaItem, i: number): string {
  const s = picked[lid]?.id === m.id;
  const v = m.type === 'video';
  const srcClass = m.source === 'Pixabay' ? 'pb' : m.source === 'Unsplash' ? 'us' : 'px';
  const srcLabel = m.source === 'Pixabay' ? 'PB' : m.source === 'Unsplash' ? 'US' : 'PX';
  return `<div class="mi${s ? ' sel' : ''}" id="mi-${lid}-${i}" onclick="pick(${lid},${i})">
    ${v
      ? `<video src="${m.videoUrl || ''}" muted loop preload="none" poster="${m.thumb || ''}" onmouseenter="this.play()" onmouseleave="this.pause();this.currentTime=0"></video>`
      : `<img src="${m.thumb || ''}" loading="lazy" alt=""/>`}
    <div class="mbadge${v ? ' v' : ''}">${v ? '▶ VIDEO' : '📷 PHOTO'}</div>
    <div class="msrc ${srcClass}">${srcLabel}</div>
    <div class="mcheck">✓</div>
    <button class="mdl" onclick="event.stopPropagation();dlOne(${lid},${i})">⬇</button>
    ${m.credit ? `<div class="mcredit">${esc(m.credit)} · ${esc(m.source)}</div>` : ''}
  </div>`;
}

/* ══════════════════════════════════════════════
   SELECT
══════════════════════════════════════════════ */
function pick(lid: number, idx: number): void {
  const line = lines.find(l => l.id === lid);
  if (!line || !line.results[idx]) return;
  const m = line.results[idx];
  const already = picked[lid]?.id === m.id;
  if (already) delete picked[lid]; else picked[lid] = m;
  line.results.forEach((_, i) => {
    document.getElementById(`mi-${lid}-${i}`)?.classList.toggle('sel', !already && i === idx);
  });
  setDot(lid, picked[lid] ? 'ok' : '');
  document.getElementById(`card-${lid}`)?.classList.toggle('matched', !!picked[lid]);
  updateDlBar();
  scheduleAutoSave();
}

function toggleSelAll(): void {
  const saAllBtn = document.getElementById('saAllBtn');
  const anyUnsel = lines.some(l => l.results.length > 0 && !picked[l.id]);
  if (anyUnsel) {
    lines.forEach(l => { if (l.results.length > 0) picked[l.id] = l.results[0]; });
    if (saAllBtn) saAllBtn.textContent = 'Deselect All';
  } else {
    picked = {};
    if (saAllBtn) saAllBtn.textContent = 'Select All';
  }
  lines.forEach(l => {
    setDot(l.id, picked[l.id] ? 'ok' : '');
    document.getElementById(`card-${l.id}`)?.classList.toggle('matched', !!picked[l.id]);
    l.results.forEach((m, i) => {
      document.getElementById(`mi-${l.id}-${i}`)?.classList.toggle('sel', picked[l.id]?.id === m.id);
    });
  });
  updateDlBar();
}

/* ══════════════════════════════════════════════
   DOWNLOAD
══════════════════════════════════════════════ */
function updateDlBar(): void {
  const n = Object.keys(picked).length;
  const any = lines.some(l => l.results.length > 0);
  const selNEl  = document.getElementById('selN');
  const dlbarEl = document.getElementById('dlbar');
  const saAllBtn = document.getElementById('saAllBtn');
  if (selNEl) selNEl.textContent = String(n);
  if (dlbarEl) dlbarEl.style.display = any ? 'flex' : 'none';
  if (saAllBtn) saAllBtn.style.display = any ? '' : 'none';
}

async function dlOne(lid: number, idx: number): Promise<void> {
  const line = lines.find(l => l.id === lid);
  if (!line) return;
  const m = line.results[idx];
  if (!m) return;
  await grab(m.download || m.thumb, `line${lid + 1}_${idx + 1}.${m.type === 'video' ? 'mp4' : 'jpg'}`);
}

async function dlSelected(): Promise<void> {
  const entries = Object.entries(picked) as [string, MediaItem][];
  if (!entries.length) { toast('Select at least one first', 'err'); return; }
  const prog = document.getElementById('dlprog');
  for (let i = 0; i < entries.length; i++) {
    const [lid, m] = entries[i];
    if (prog) prog.textContent = `Downloading ${i + 1}/${entries.length}…`;
    await grab(m.download || m.thumb, `line${parseInt(lid) + 1}.${m.type === 'video' ? 'mp4' : 'jpg'}`);
    await wait(200);
  }
  if (prog) { prog.textContent = `✓ ${entries.length} downloaded`; setTimeout(() => { if (prog) prog.textContent = ''; }, 3000); }
}

async function dlAll(): Promise<void> {
  const targets = lines.filter(l => l.results.length > 0).map(l => ({ lid: l.id, m: picked[l.id] || l.results[0] }));
  if (!targets.length) { toast('Search some lines first', 'err'); return; }
  const prog = document.getElementById('dlprog');
  for (let i = 0; i < targets.length; i++) {
    const { lid, m } = targets[i];
    if (prog) prog.textContent = `Downloading ${i + 1}/${targets.length}…`;
    await grab(m.download || m.thumb, `line${lid + 1}.${m.type === 'video' ? 'mp4' : 'jpg'}`);
    await wait(220);
  }
  if (prog) { prog.textContent = `✓ All ${targets.length} downloaded`; setTimeout(() => { if (prog) prog.textContent = ''; }, 3000); }
}

async function grab(url: string, name: string): Promise<void> {
  try {
    const r = await fetch(url, { mode: 'cors' });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, '_blank');
  }
}

/* ══════════════════════════════════════════════
   UTILS
══════════════════════════════════════════════ */
function clearAll(): void {
  lines = []; picked = {}; busy.clear(); uid = 0;
  const linesList = document.getElementById('linesList');
  const rscroll   = document.getElementById('rscroll');
  if (linesList) linesList.innerHTML = '';
  if (rscroll) rscroll.innerHTML = `<div class="empty-r" id="emptyEl"><div class="empty-icon">🎬</div><div class="empty-txt">Paste or type your script.<br>Each line gets a keyword — edit &amp; search.<br><br>Free tier: <strong style="color:var(--amber)">2 searches/day</strong> · 300 char script limit.</div></div>`;
  const lcount = document.getElementById('lcount');
  const saBtn  = document.getElementById('saBtn') as HTMLButtonElement | null;
  const dlbar  = document.getElementById('dlbar');
  const saAllBtn = document.getElementById('saAllBtn');
  const pasteTA  = document.getElementById('pasteTA') as HTMLTextAreaElement | null;
  const typeTA   = document.getElementById('typeTA')  as HTMLTextAreaElement | null;
  if (lcount) lcount.textContent = '0 lines';
  if (saBtn) saBtn.disabled = true;
  if (dlbar) dlbar.style.display = 'none';
  if (saAllBtn) saAllBtn.style.display = 'none';
  if (pasteTA) pasteTA.value = '';
  if (typeTA) typeTA.value = '';
  updateCharCount();
}

function showRight(): void { document.getElementById('emptyEl')?.remove(); updateDlBar(); }

function focusRes(id: number): void {
  document.querySelectorAll<HTMLElement>('.lc').forEach(c => c.classList.remove('active'));
  document.getElementById(`card-${id}`)?.classList.add('active');
  document.getElementById(`res-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function setDot(id: number, s: 'ok' | 'spin' | ''): void {
  const d = document.getElementById(`dot-${id}`);
  if (!d) return;
  d.className = 'sdot' + (s === 'ok' ? ' ok' : s === 'spin' ? ' spin' : '');
}

function esc(s: unknown): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function escA(s: unknown): string {
  return String(s).replace(/"/g, '&quot;');
}

function enc(s: string): string { return encodeURIComponent(s); }
function wait(ms: number): Promise<void> { return new Promise(r => setTimeout(r, ms)); }

function toast(msg: string, type = ''): void {
  const el = document.getElementById('toastEl');
  if (!el) return;
  el.textContent = msg;
  el.className = 'toast show' + (type ? ' ' + type : '');
  if (_toastTimer) clearTimeout(_toastTimer);
  _toastTimer = setTimeout(() => { el.className = 'toast'; }, 3000);
}

/* ══════════════════════════════════════════════
   WAITLIST
══════════════════════════════════════════════ */
function showWaitlistModal(): void {
  document.getElementById('waitlistModal')?.classList.add('show');
  setTimeout(() => (document.getElementById('wlEmailInput') as HTMLInputElement | null)?.focus(), 100);
}

function closeWaitlistModal(): void {
  document.getElementById('waitlistModal')?.classList.remove('show');
}

async function submitWaitlist(): Promise<void> {
  const emailEl = document.getElementById('wlEmailInput') as HTMLInputElement | null;
  const email = emailEl?.value.trim() ?? '';
  if (!email || !email.includes('@')) { emailEl?.focus(); return; }
  const btn = document.querySelector<HTMLButtonElement>('#wlInputRow .btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Joining…'; }
  try {
    await sb.from('waitlist').insert({ email, created_at: new Date().toISOString() });
  } catch { /* graceful */ }
  // Email notification to support
  try {
    const subject = encodeURIComponent('🚀 New Sceneify Pro Waitlist Signup');
    const body = encodeURIComponent(`New waitlist signup:\n\nEmail: ${email}\nTime: ${new Date().toLocaleString()}\n\nTotal signups growing!`);
    const link = document.createElement('a');
    link.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    link.style.display = 'none';
    document.body.appendChild(link); link.click(); link.remove();
  } catch { /* ignore */ }
  const wlInputRow = document.getElementById('wlInputRow');
  const wlOk = document.getElementById('wlOk');
  if (wlInputRow) wlInputRow.style.display = 'none';
  if (wlOk) wlOk.style.display = 'block';
  toast("You're on the list! 🎉", 'ok');
  setTimeout(() => closeWaitlistModal(), 4000);
}

/* ══════════════════════════════════════════════
   SUPPORT — opens mailto to support email
══════════════════════════════════════════════ */
function openSupport(): void {
  const subject = encodeURIComponent('Sceneify Support Request');
  const body = encodeURIComponent(`Hi Sceneify team,\n\nI need help with:\n\n[Describe your issue here]\n\nUser: ${currentUser?.email || 'Not logged in'}\n`);
  window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
}

function openFeatureRequest(): void {
  const subject = encodeURIComponent('Sceneify Feature Request');
  const body = encodeURIComponent(`Hi Sceneify team,\n\nI have a feature request:\n\n[Describe your idea here]\n\nUser: ${currentUser?.email || 'Not logged in'}\n`);
  window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
}

/* ══════════════════════════════════════════════
   ADMIN PANEL
══════════════════════════════════════════════ */
function openAdmin(): void {
  if (currentUser?.email !== ADMIN_EMAIL) return;
  document.getElementById('adminModal')?.classList.add('show');
  loadAdminData();
}

function closeAdmin(): void {
  document.getElementById('adminModal')?.classList.remove('show');
}

async function loadAdminData(): Promise<void> {
  const list = document.getElementById('adminList');
  if (!list) return;
  list.innerHTML = '<div class="admin-empty">Loading…</div>';
  try {
    const { data, error } = await sb
      .from('waitlist')
      .select('email, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    const entries = (data as WaitlistEntry[]) || [];
    const now = new Date();
    const todayStr = now.toDateString();
    const weekAgo = new Date(now.getTime() - 7 * 864e5);
    const adminTotal = document.getElementById('adminTotal');
    const adminToday = document.getElementById('adminToday');
    const adminWeek  = document.getElementById('adminWeek');
    if (adminTotal) adminTotal.textContent = String(entries.length);
    if (adminToday) adminToday.textContent = String(entries.filter(e => new Date(e.created_at).toDateString() === todayStr).length);
    if (adminWeek)  adminWeek.textContent  = String(entries.filter(e => new Date(e.created_at) >= weekAgo).length);
    if (!entries.length) {
      list.innerHTML = '<div class="admin-empty">No waitlist signups yet.<br>Share that Pro pricing button!</div>';
      return;
    }
    list.innerHTML = entries.map(e => {
      const d = new Date(e.created_at);
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      return `<div class="admin-row">
        <span class="admin-email">${esc(e.email)}</span>
        <span class="admin-date">${dateStr} · ${timeStr}</span>
      </div>`;
    }).join('');
  } catch (e) {
    if (list) list.innerHTML = `<div class="admin-empty">Error: ${esc((e as Error).message)}<br><small>Run waitlist_table.sql in Supabase first.</small></div>`;
  }
}

function exportWaitlistCSV(): void {
  const rows = document.querySelectorAll<HTMLElement>('#adminList .admin-row');
  if (!rows.length) { toast('No data to export', 'err'); return; }
  let csv = 'Email,Signed Up\n';
  rows.forEach(row => {
    const email = row.querySelector<HTMLElement>('.admin-email')?.textContent?.trim() ?? '';
    const date  = row.querySelector<HTMLElement>('.admin-date')?.textContent?.trim() ?? '';
    csv += `${email},"${date}"\n`;
  });
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `sceneify-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
  toast('Waitlist exported!', 'ok');
}

/* ══════════════════════════════════════════════
   EXPORT CSV
══════════════════════════════════════════════ */
function exportCSV(): void {
  if (!lines.length) { toast('No lines to export yet', 'err'); return; }
  const rows: (string | number)[][] = [['Line #', 'Script Text', 'Keyword', 'Selected Clip URL', 'Clip Type', 'Photographer/Creator']];
  lines.forEach(l => {
    const sel = picked[l.id];
    rows.push([
      l.id + 1,
      `"${l.text.replace(/"/g, '""')}"`,
      `"${(l.keyword || '').replace(/"/g, '""')}"`,
      sel ? (sel.download || sel.thumb || '') : '',
      sel ? sel.type : '',
      sel ? (sel.credit || '') : '',
    ]);
  });
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'sceneify-export.csv';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
  toast('CSV exported!', 'ok');
}

/* ══════════════════════════════════════════════
   FINGERPRINT / BYPASS PREVENTION
   Fixed: new account gets fresh fingerprint key
══════════════════════════════════════════════ */
function getFingerprint(): string {
  const nav = window.navigator;
  const parts = [
    nav.userAgent, nav.language, String(nav.hardwareConcurrency),
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    nav.platform,
  ].join('|');
  let h = 0;
  for (let i = 0; i < parts.length; i++) {
    h = Math.imul(31, h) + parts.charCodeAt(i) | 0;
  }
  return Math.abs(h).toString(36);
}

// Include user ID in fingerprint key so different accounts don't share limits
function getFpKey(): string {
  const userId = currentUser?.id?.slice(0, 8) || 'anon';
  return `fp_${userId}_${getFingerprint()}_${new Date().toDateString()}`;
}
function getFpCount(): number { try { return parseInt(localStorage.getItem(getFpKey()) || '0'); } catch { return 0; } }
function incFpCount(): void {
  try {
    const k = getFpKey();
    localStorage.setItem(k, String(getFpCount() + 1));
    // Clean up old keys (different days)
    Object.keys(localStorage)
      .filter(key => key.startsWith('fp_') && !key.includes(new Date().toDateString()))
      .forEach(key => localStorage.removeItem(key));
  } catch { /* ignore */ }
}

/* ══════════════════════════════════════════════
   LARGE PREVIEW MODAL
══════════════════════════════════════════════ */
function openPreview(thumb: string, videoUrl: string | undefined, isVideo: boolean, credit: string): void {
  const modal = document.getElementById('previewModal');
  const container = document.getElementById('previewContainer');
  const creditEl = document.getElementById('previewCredit');
  if (!modal || !container) return;
  container.innerHTML = isVideo
    ? `<video src="${escA(videoUrl || '')}" controls autoplay muted loop style="max-width:100%;max-height:80vh;border-radius:8px"></video>`
    : `<img src="${escA(thumb)}" style="max-width:100%;max-height:80vh;border-radius:8px;object-fit:contain" alt=""/>`;
  if (creditEl) creditEl.textContent = credit || '';
  modal.classList.add('show');
}

function closePreview(): void {
  const modal = document.getElementById('previewModal');
  const container = document.getElementById('previewContainer');
  if (container) container.innerHTML = '';
  modal?.classList.remove('show');
}

/* ══════════════════════════════════════════════
   LANDING PAGE ANIMATIONS
══════════════════════════════════════════════ */
function initLandingAnimations(): void {
  document.querySelectorAll<HTMLElement>('.demo-row').forEach((row, i) => {
    setTimeout(() => row.classList.add('visible'), 400 + i * 300);
  });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); }
    });
  }, { threshold: 0.1 });
  document.querySelectorAll<HTMLElement>('.fade-up').forEach(el => observer.observe(el));

  function animateStat(statEl: HTMLElement): void {
    const el = statEl.querySelector<HTMLElement>('.stat-num[data-target]');
    if (!el || el.dataset.animated) return;
    el.dataset.animated = '1';
    const target = parseInt(el.dataset.target!);
    const suffix = el.dataset.suffix || '';
    const dur = 1600;
    const start = performance.now();
    function tick(now: number): void {
      if (!el) return;
      const p = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const val = Math.round(ease * target);
      el.textContent = (val >= 1000 ? (val / 1000).toFixed(val >= 10000 ? 0 : 1) + 'K' : String(val)) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  const counterObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      animateStat(e.target as HTMLElement);
      counterObserver.unobserve(e.target);
    });
  }, { threshold: 0.2 });

  document.querySelectorAll<HTMLElement>('.stat').forEach(el => {
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setTimeout(() => animateStat(el), 300);
    } else {
      counterObserver.observe(el);
    }
  });

  const liveEl = document.getElementById('liveCount');
  if (liveEl) {
    setInterval(() => { liveEl.textContent = String(230 + Math.floor(Math.random() * 40)); }, 4000);
  }
}

function tryInitLanding(): void {
  const landing = document.getElementById('landingPage');
  if (landing && landing.classList.contains('active')) initLandingAnimations();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tryInitLanding);
} else {
  requestAnimationFrame(() => requestAnimationFrame(tryInitLanding));
}

/* ══════════════════════════════════════════════
   KEYBOARD LISTENER
══════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  const typeTA = document.getElementById('typeTA') as HTMLTextAreaElement | null;
  typeTA?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doAdd(); }
  });
});

/* ══════════════════════════════════════════════
   PASSWORD HELPERS
══════════════════════════════════════════════ */
function togglePw(inputId: string, btn: HTMLButtonElement): void {
  const input = document.getElementById(inputId) as HTMLInputElement | null;
  if (!input) return;
  const show = input.type === 'password';
  input.type = show ? 'text' : 'password';
  btn.textContent = show ? '🙈' : '👁';
}

function checkStrength(input: HTMLInputElement): void {
  const v = input.value;
  const bar  = document.getElementById('pwStrengthBar');
  const fill = document.getElementById('pwStrengthFill') as HTMLElement | null;
  const hint = document.getElementById('pwHint') as HTMLElement | null;
  if (!bar) return;
  bar.style.display  = v.length ? 'block' : 'none';
  if (hint) hint.style.display = v.length ? 'block' : 'none';
  let s = 0;
  if (v.length >= 8)  s++;
  if (v.length >= 12) s++;
  if (/[A-Z]/.test(v)) s++;
  if (/[0-9]/.test(v)) s++;
  if (/[^A-Za-z0-9]/.test(v)) s++;
  const levels = [
    { w: '20%', c: '#ff4455', t: 'Too short' },
    { w: '40%', c: '#ff4455', t: 'Weak' },
    { w: '60%', c: 'var(--amber)', t: 'Fair' },
    { w: '80%', c: 'var(--amber)', t: 'Good' },
    { w: '100%', c: 'var(--green)', t: 'Strong 💪' },
  ];
  const lvl = levels[Math.min(s, 4)];
  if (fill) { fill.style.width = lvl.w; fill.style.background = lvl.c; }
  if (hint) { hint.textContent = lvl.t; hint.style.color = lvl.c; }
}

/* ══════════════════════════════════════════════
   WELCOME ANIMATION
══════════════════════════════════════════════ */
function showWelcome(msg: string, sub: string, icon: string): void {
  const overlay = document.getElementById('welcomeOverlay');
  if (!overlay) return;
  const msgEl  = document.getElementById('wlcMsg');
  const subEl  = document.getElementById('wlcSub');
  const iconEl = document.getElementById('wlcIcon');
  if (msgEl)  msgEl.textContent  = msg;
  if (subEl)  subEl.textContent  = sub;
  if (iconEl) iconEl.textContent = icon;
  overlay.classList.remove('show');
  void overlay.offsetWidth;
  overlay.classList.add('show');
  setTimeout(() => overlay.classList.remove('show'), 2300);
}

function spawnParticles(): void {
  const colors = ['#00e8a2', '#4d9fff', '#f5a623', '#ff4455', '#fff'];
  const container = document.getElementById('authPage');
  if (!container) return;
  for (let i = 0; i < 30; i++) {
    const p = document.createElement('div');
    p.className = 'auth-particle';
    const size  = 5 + Math.random() * 8;
    const tx    = (Math.random() - 0.5) * 360;
    const ty    = -(120 + Math.random() * 320);
    const dur   = 0.9 + Math.random() * 1.1;
    const delay = Math.random() * 0.4;
    p.style.cssText = `width:${size}px;height:${size}px;left:${20 + Math.random() * 60}%;bottom:45%;background:${colors[i % colors.length]};--tx:${tx}px;--ty:${ty}px;animation-duration:${dur}s;animation-delay:${delay}s`;
    container.appendChild(p);
    setTimeout(() => p.remove(), (dur + delay + 0.2) * 1000);
  }
}

/* ══════════════════════════════════════════════
   EXPOSE GLOBALS
══════════════════════════════════════════════ */
declare global {
  interface Window {
    showPage: typeof showPage;
    showAuth: typeof showAuth;
    switchAuthTab: typeof switchAuthTab;
    doLogin: typeof doLogin;
    doSignup: typeof doSignup;
    doLogout: typeof doLogout;
    showLogoutSurvey: typeof showLogoutSurvey;
    closeLogoutSurvey: typeof closeLogoutSurvey;
    submitLogoutSurvey: typeof submitLogoutSurvey;
    skipLogoutSurvey: typeof skipLogoutSurvey;
    toggleSettingsMenu: typeof toggleSettingsMenu;
    switchTab: typeof switchTab;
    doPaste: typeof doPaste;
    doAdd: typeof doAdd;
    removeLine: typeof removeLine;
    searchLine: typeof searchLine;
    searchAll: typeof searchAll;
    reSearch: typeof reSearch;
    filterBlock: typeof filterBlock;
    pick: typeof pick;
    toggleSelAll: typeof toggleSelAll;
    dlOne: typeof dlOne;
    dlSelected: typeof dlSelected;
    dlAll: typeof dlAll;
    clearAll: typeof clearAll;
    focusRes: typeof focusRes;
    updateCharCount: typeof updateCharCount;
    toggleProjPanel: typeof toggleProjPanel;
    saveProject: typeof saveProject;
    loadProject: typeof loadProject;
    deleteProject: typeof deleteProject;
    newProject: typeof newProject;
    closeModal: typeof closeModal;
    showWaitlistModal: typeof showWaitlistModal;
    closeWaitlistModal: typeof closeWaitlistModal;
    submitWaitlist: typeof submitWaitlist;
    openAdmin: typeof openAdmin;
    closeAdmin: typeof closeAdmin;
    loadAdminData: typeof loadAdminData;
    exportWaitlistCSV: typeof exportWaitlistCSV;
    exportCSV: typeof exportCSV;
    togglePw: typeof togglePw;
    checkStrength: typeof checkStrength;
    openSupport: typeof openSupport;
    openFeatureRequest: typeof openFeatureRequest;
    openPreview: typeof openPreview;
    closePreview: typeof closePreview;
  }
}

Object.assign(window, {
  showPage, showAuth, switchAuthTab, doLogin, doSignup, doLogout,
  showLogoutSurvey, closeLogoutSurvey, submitLogoutSurvey, skipLogoutSurvey,
  toggleSettingsMenu,
  switchTab, doPaste, doAdd, removeLine, searchLine, searchAll, reSearch,
  filterBlock, pick, toggleSelAll, dlOne, dlSelected, dlAll, clearAll,
  focusRes, updateCharCount, toggleProjPanel, saveProject, loadProject,
  deleteProject, newProject, closeModal, showWaitlistModal, closeWaitlistModal,
  submitWaitlist, openAdmin, closeAdmin, loadAdminData, exportWaitlistCSV,
  exportCSV, togglePw, checkStrength, openSupport, openFeatureRequest,
  openPreview, closePreview,
});
